/**
 * @file oxpay-supabase.js
 * @description OxPay-Supabase 통합 레이어 - 두 서비스 간 데이터 동기화
 *
 * 이 모듈은 OxPay와 Supabase 간의 사용자 계정 연동을 담당합니다.
 * 사용자 메타데이터 동기화와 계정 생성 로직을 처리합니다.
 *
 * 주요 책임:
 * - OxPay 계정 생성 및 Supabase 메타데이터 업데이트
 * - KYC 상태 동기화
 * - 사용자 계정 연결 관리
 * - 에러 처리 및 복구 로직
 *
 * 데이터 흐름:
 * 1. Supabase 사용자 생성 → 2. OxPay 계정 생성 → 3. 메타데이터 동기화
 *
 * 사용 예시:
 * - 회원가입 후 자동 OxPay 계정 생성
 * - KYC 완료 후 상태 업데이트
 * - 기존 OxPay 계정 연결
 *
 * @module lib/oxpay-supabase
 */

import { supabase } from "@/lib/supabase";
import { oxpayAPI } from "@/lib/oxpay";

/**
 * Create OxPay account and sync with Supabase
 */
export async function createOxPayAccount(supabaseUser, additionalData = {}) {
  // Prepare user data
  const userData = {
    email: supabaseUser.email,
    firstName:
      supabaseUser.user_metadata?.firstName || additionalData.firstName || "",
    lastName:
      supabaseUser.user_metadata?.lastName || additionalData.lastName || "",
    countryCode: additionalData.countryCode || "US",
    mobileNumber: additionalData.mobileNumber || "",
  };

  try {
    // Create OxPay account
    const response = await oxpayAPI.createUser(userData);

    // Check if successful (API returns user data directly)
    if (response.userId) {
      const oxpayUserId = response.userId;

      // Update Supabase metadata
      await updateSupabaseMetadata(supabaseUser, {
        oxpayUserId,
        oxpayAccountCreated: new Date().toISOString(),
      });

      return {
        success: true,
        oxpayUserId,
        data: response,
      };
    }

    // Handle errors
    return {
      success: false,
      error: response.error || "Failed to create user account",
    };
  } catch (error) {
    // Check for specific error cases
    if (error.message?.includes("already exists")) {
      return {
        success: false,
        error: "USER_EXISTS",
        message: "Account already exists. Please contact support.",
      };
    }

    console.error("Error creating OxPay account:", error);
    return {
      success: false,
      error: error.message || "An unexpected error occurred",
    };
  }
}

/**
 * Update Supabase user metadata
 */
async function updateSupabaseMetadata(user, updates) {
  const { error } = await supabase.auth.updateUser({
    data: {
      ...user.user_metadata,
      ...updates,
    },
  });

  if (error) {
    console.error("Failed to update Supabase metadata:", error);
    return false;
  }

  return true;
}

/**
 * Check if user has OxPay account
 */
export function hasOxPayAccount(user) {
  return !!user?.user_metadata?.oxpayUserId;
}

/**
 * Get OxPay user ID from Supabase user
 */
export function getOxPayUserId(user) {
  return user?.user_metadata?.oxpayUserId || null;
}

/**
 * Sync KYC status from OxPay to Supabase
 */
export async function syncKYCStatus(user) {
  const oxpayUserId = getOxPayUserId(user);
  if (!oxpayUserId) return null;

  try {
    const kycData = await oxpayAPI.getKYCStatus(oxpayUserId);

    if (kycData?.status) {
      await updateSupabaseMetadata(user, {
        kycStatus: kycData.status,
        kycLastChecked: new Date().toISOString(),
      });

      return kycData.status;
    }

    return null;
  } catch (error) {
    // Don't throw error, just log and return null
    // This prevents the app from breaking if OxPay API is down
    console.warn(
      "Could not sync KYC status (API may be unavailable):",
      error.message
    );
    return null;
  }
}

/**
 * Link existing OxPay account to Supabase user
 * (For users who may have registered on OxPay separately)
 */
export async function linkExistingOxPayAccount(user, oxpayUserId) {
  try {
    // Verify the account exists and belongs to this user
    const oxpayUser = await oxpayAPI.getUser(oxpayUserId);

    // Check email matches
    if (oxpayUser.email !== user.email) {
      return {
        success: false,
        error: "Email mismatch. This user account belongs to a different user.",
      };
    }

    // Link the account
    await updateSupabaseMetadata(user, {
      oxpayUserId,
      oxpayLinkedAt: new Date().toISOString(),
    });

    return {
      success: true,
      oxpayUserId,
    };
  } catch (error) {
    console.error("Error linking user account:", error);
    return {
      success: false,
      error: error.message || "Failed to link account",
    };
  }
}
